import * as React from 'react';
import * as moment from 'moment';
import {connect} from 'react-redux';
import {push} from 'react-router-redux';
import {bindActionCreators} from 'redux';
import * as Commonmark from 'react-commonmark';
import {IStore} from '../../../../redux/IStore';
import Loading from '../../../../components/Loading';
import FaInfoCircle from 'react-icons/fa/info-circle';
import NoCoverAvailable from '../SchemeChoice/components/NoCoverAvailable';
import {IAuthState} from '../../../../services/auth/models';
import SelectControl from '../../../../components/SelectControl';
import {II18nMessages} from '../../../../services/branding/models';
import FieldFormControl from '../../../../components/FieldFormControl';
import {recalculateScore} from '../../../../services/healix/reducer';
import {actions as quoteActions} from '../../../../services/quote/reducer';
import {IQuoteActions, IQuoteState} from '../../../../services/quote/models';
import AlternativeDatePicker from '../../../../components/AlternativeDatePicker';
import {difference, flow, forEach, isEmpty, map, size} from 'lodash';
import get = require('lodash/get');
import values = require('lodash/values');
import {annualLimit, isFutureDate, isRequired, isValidDate} from '../../../../util/validators';
import {IBenefit, IChannel, IOptionMap, IPolicyApplication, IProduct} from '../../../../data/api/models';
import {EventWithDataHandler, Field, formValueSelector, InjectedFormProps, reduxForm, submit} from 'redux-form';
import {Button, Col, ControlLabel, Form, FormGroup, Modal, OverlayTrigger, Popover, Row} from 'react-bootstrap';
import MaxTripDuration from '../TripDetails/components/MaxTripDuration/MaxTripDuration';
import FaChevronRight from 'react-icons/fa/chevron-right';
import QuoteCard from './components/QuoteCard';
import {QuoteOrder, getQuoteOrder} from './schemeChoice.helpers';
import {mergeOptionsIntoCache, mergeQuotesIntoCache} from './schemeChoice.cache';
import {getAge, isQuoteOkForDisplay, parseDob, shouldShowAnnualTile} from './schemeChoice.rules';
import {
    buildSideBySideVm,
    buildStandardQuoteColumnsVm,
    getOrderedTripTypesList,
    IQuoteCardVm,
    IQuoteColumnVm,
} from './schemeChoice.viewModels';
import HazardousPackSelector from './components/HazardousPackSelector';
import OptionsList from './components/OptionsList';
import LeftSideHelper from './components/LeftSideHelper';
import {AvailableOptionsMap, splitOptions} from './schemeChoice.options';
import {scrollToFirstError} from '../../../../util/scrollToFirstError';

let persistedQuotesCache: { [coverLevelKey: string]: { [tripTypeKey: string]: any } } = {};
let persistedOptionsCache: { [key: string]: any } = {};

interface IQuotesCache { [coverLevelKey: string]: { [tripTypeKey: string]: any }; }

interface ISchemeChoiceFormOwnProps {
    i18n: II18nMessages;
    quote: IQuoteState;
    application: IPolicyApplication;
    tripType: string;
    schemeId: string;
    startDate: string;
    password: string;
    passwordConfirmation: string;
    auth: IAuthState;
    endDate: string;
    pristine: boolean;
    submitting: boolean;
    region: string;
    maxTripDuration: string;
    dispatch: any;
    onBack: () => void;
    channel: IChannel;
    options: IOptionMap;

    renderDefinition(name: string, defName?: string, icon?: string): any;

    recalculateScore(callback, screeningId, region, destinations);

    product: IProduct;
    actions: {
        submit(name: string);
        push: (route: string) => void;
        quote: IQuoteActions;
        getFormSubmitErrors: (formName: string) => void;
        getFormError: (formName: string) => any;
    };
}

type ISchemeChoiceFormProps =
    ISchemeChoiceFormOwnProps &
    InjectedFormProps<IPolicyApplication, ISchemeChoiceFormOwnProps>;

interface ISchemeChoiceFormState {
    invalidFields: boolean;
    moreInfo: boolean;
    showUpSellModal: boolean;
    showSaveQuoteModal: boolean;
    isQuoteSaved: boolean;
    columns: number;
    coverLevel: string;
    moreInfoSign: string;
    showPartCMessageModal: boolean;
    quoteOrder: QuoteOrder;
    quotesCache: IQuotesCache;
    optionsCache: { [key: string]: any };
}

interface ISchemeChoiceVm {
    quotesBySchemeId: IQuotesCache;
    orderedTripTypes: any[];
    hasFinishedCalculation: boolean;
    noAvailableQuotes: boolean;
    coverChoices: React.ReactNode;
    renderCallMessage: string;
    mappedAllObjects: Record<string, any>;
    hazardousOptions: any[];
    selectedBooleans: { [key: string]: boolean };
    selectedHazardPack: string;
    showAdditionalOptions: boolean;
}

class SchemeChoiceForm extends React.Component<ISchemeChoiceFormProps, ISchemeChoiceFormState> {
    public state = {
        invalidFields: false,
        moreInfo: false,
        showUpSellModal: false,
        showSaveQuoteModal: false,
        isQuoteSaved: false,
        columns: 0,
        coverLevel: '',
        moreInfoSign: 'glyphicon-plus-sign',
        showPartCMessageModal: false,
        quoteOrder: getQuoteOrder(this.props.product.productCode),
        quotesCache: persistedQuotesCache || {},
        optionsCache: persistedOptionsCache || {},
    };

    private lastReferralKey: string | null = null;

    public componentDidMount() {
        const {dispatch, schemeId, actions, change} = this.props;

        dispatch(change('steps', ['tripDetails', 'schemeChoice', 'declaration']));

        if (schemeId) {
            actions.quote.calculate(null, true);
        }
    }

    public componentWillUnmount() {
        persistedQuotesCache = this.state.quotesCache;
        persistedOptionsCache = this.state.optionsCache;
    }

    public componentDidUpdate(prevProps: ISchemeChoiceFormProps) {
        const tripType = this.props.tripType;
        if (!tripType) {
            return;
        }

        const quotesChanged = prevProps.quote.quotes !== this.props.quote.quotes;
        const tripTypeChanged = prevProps.tripType !== this.props.tripType;
        const optionsChanged = prevProps.options !== this.props.options;

        if (!(quotesChanged || tripTypeChanged || optionsChanged)) {
            return;
        }

        const nextState: Partial<ISchemeChoiceFormState> = {};

        if (quotesChanged || tripTypeChanged) {
            let cols = 0;
            const allReferrals: any[] = [];

            const stableQuotes = this.getStableQuotes();

            forEach(stableQuotes, (tripTypes) => {
                const q = tripTypes && tripTypes[tripType];

                if (!q) { return; }

                if (isQuoteOkForDisplay(q)) {
                    cols++;
                }

                if (q.referrals && q.referrals.length > 0) {
                    allReferrals.push(...q.referrals);
                }
            });

            const referralKey = allReferrals
                .map((r) => (r && (r.id || r.code || JSON.stringify(r))))
                .sort()
                .join('|');

            if (allReferrals.length > 0 && referralKey !== this.lastReferralKey) {
                this.lastReferralKey = referralKey;

                this.props.actions.quote.referralQuote(
                    this.props.quote.application.id,
                    allReferrals,
                );
            }

            if (cols !== this.state.columns) {
                (nextState as any).columns = cols;
            }

            const nextQuotesCache = mergeQuotesIntoCache(
                this.state.quotesCache,
                this.props.quote.quotes,
            );

            if (nextQuotesCache !== this.state.quotesCache) {
                (nextState as any).quotesCache = nextQuotesCache;
            }
        }

        if (optionsChanged) {
            const nextOptionsCache = mergeOptionsIntoCache(
                this.state.optionsCache,
                this.props.options,
            );

            if (nextOptionsCache !== this.state.optionsCache) {
                (nextState as any).optionsCache = nextOptionsCache;
            }
        }

        if (Object.keys(nextState).length > 0) {
            this.setState(nextState as any, () => {
                persistedQuotesCache = this.state.quotesCache;
                persistedOptionsCache = this.state.optionsCache;
            });
        }
    }

    private handleSubmit = () => {
        const {change, dispatch, actions, region, maxTripDuration, product} = this.props;
        const annualTripDurations = Object.keys(get(product, 'metadata.annual_trip_durations', []));
        const checkMaxTrip = annualTripDurations.length > 1;

        if (!region || (checkMaxTrip && !maxTripDuration)) {
            this.setState({
                invalidFields: true,
            });
            return;
        }

        dispatch(change('endDate', ''));
        dispatch(change('destinations', []));
        dispatch(change('tripType', 'annual'));

        setTimeout(() => {
            actions.quote.saveQuote(true);
            actions.submit('quote');
            this.setState({
                showUpSellModal: false,
            });
        }, 400);
    }

    private buildVm(): ISchemeChoiceVm {
        const { product, schemeId } = this.props;

        const quotesBySchemeId = this.getStableQuotes();

        const orderedTripTypes = getOrderedTripTypesList({
            product,
            quotesBySchemeId,
        });

        const hasFinishedCalculation = values(quotesBySchemeId).length >= 1;
        const noAvailableQuotes = isEmpty(quotesBySchemeId);

        const coverChoices = this.renderQuotes(orderedTripTypes);

        const callMessage = {
            free: 'Simply call our UK-based specialist customer service team FREE on',
            cost: 'Simply call our UK-based specialist customer service team on',
        };

        const productCallMessage: Record<string, string> = {
            CSIS: callMessage.cost,
            TPL: callMessage.cost,
            CAMC: callMessage.cost,
        };

        const renderCallMessage = productCallMessage[product.productCode] || callMessage.cost;

        const allOptions = this.getAllOptions();
        const { mappedAllObjects, hazardousOptions } = splitOptions(allOptions);

        const selectedBooleans = this.getSelectedBooleanOptions();
        const selectedHazardPack = this.getSelectedHazardPack();

        const showAdditionalOptions = Boolean(schemeId) && hazardousOptions.length > 0;

        return {
            quotesBySchemeId,
            orderedTripTypes,
            hasFinishedCalculation,
            noAvailableQuotes,
            coverChoices,
            renderCallMessage,
            mappedAllObjects,
            hazardousOptions,
            selectedBooleans,
            selectedHazardPack,
            showAdditionalOptions,
        };
    }

    private handleModalSubmit = () => {
        const {change, dispatch, region, maxTripDuration, product} = this.props;
        const annualTripDurations = Object.keys(get(product, 'metadata.annual_trip_durations', []));
        const checkMaxTrip = annualTripDurations.length > 1;

        if (!region || (checkMaxTrip && !maxTripDuration)) {
            this.setState({
                invalidFields: true,
            });
            return;
        }

        dispatch(change('endDate', ''));
        dispatch(change('destinations', []));
        dispatch(change('tripType', 'annual'));

        setTimeout(() => {
            this.setState({
                showUpSellModal: false,
            });
        }, 400);
    }

    private handleQuestionOptionClick = (key: string) => () => {
        const { change } = this.props;
        const opts = this.getStableOptions() as Record<string, boolean | string | undefined>;

        const current = !!opts[key];
        const next = !current;

        change('options.' + key, next);

        const nextOptionsCache = { ...this.state.optionsCache, [key]: next };
        this.setState({ optionsCache: nextOptionsCache } as any, () => {
            persistedOptionsCache = this.state.optionsCache;
        });

        const mutuallyExclusive: Record<string, string> = {
            increase_cancellation_15: 'increase_cancellation_10',
            increase_cancellation_10: 'increase_cancellation_15',
            trip_extension_45: 'trip_extension_60',
            trip_extension_60: 'trip_extension_45',
        };

        const otherKey = mutuallyExclusive[key];

        if (otherKey && next && opts[otherKey]) {
            change(`options.${otherKey}`, false);
            nextOptionsCache[otherKey] = false;
        }

        // Trip duration logic
        if (key === 'trip_extension_45' || key === 'trip_extension_60') {
            let duration = '33';

            if (key === 'trip_extension_45' && next) {
                duration = '45';
            } else if (key === 'trip_extension_60' && next) {
                duration = '60';
            }

            change('maxTripDuration', duration);
        }

        this.recalculatePrice();
    }
    private checkForUpSell = (checkSchemeId?: string) => {
        const {quote, schemeId: formSchemeId, tripType, dispatch, change} = this.props;
        const schemeId = checkSchemeId ? checkSchemeId : formSchemeId;

        forEach(quote.quotes, (quote) => {
            const annualQuote = quote && quote.annual && quote.annual;

            if (tripType === 'single' && schemeId === get(annualQuote, 'scheme.id')) {
                dispatch(change('region', annualQuote.information.region));
                this.setState({
                    showUpSellModal: true,
                    coverLevel: annualQuote.scheme.coverLevel,
                });
            }
        });
    }

    private closeUpSellModal = () => {
        const {change, dispatch} = this.props;
        dispatch(change('schemeId', ''));
        this.setState({
            showUpSellModal: false,
        });
    }

    private closeUpSellModalB = () => {
        const {change, dispatch} = this.props;
        dispatch(change('options.part_b', false));
        dispatch(change('options.part_c', false));
        this.setState({
            showPartCMessageModal: false,
        });
    }

    private recalculatePrice = () => {
        const {actions} = this.props;
        setTimeout((() => {
            actions.quote.calculate(null, true);
        }), 300);
    }

    private renderPartCMessageModal = () => {
        return (
            <Modal show={this.state.showPartCMessageModal} onHide={this.closeUpSellModalB}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>Would You Like to Continue</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="section-group">
                        <div className="section">
                            Part C can only be selected with Part B, if you are happy with this please continue with
                            your quote.
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        bsStyle="primary"
                        className="btn-remove-border"
                        type="submit"
                        onClick={this.handleSubmit}
                    >
                        Continue
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    private renderUpSellModal = () => {
        const {i18n, product} = this.props;
        const {coverLevel} = this.state;
        const modalTitle = 'Annual Multi-Trip';

        let regionUk = 'UK';
        let regionEurope = 'Europe';
        let regionWorldwideExcluding = 'Worldwide Excluding';
        let regionWorldwide = 'Worldwide';

        if (product.metadata.definitions.region_uk) {
            regionUk = product.metadata.definitions.region_uk;
        }
        if (product.metadata.definitions.region_europe) {
            regionEurope = product.metadata.definitions.region_europe;
        }
        if (product.metadata.definitions.region_worldwide_excluding) {
            regionWorldwideExcluding = product.metadata.definitions.region_worldwide_excluding;
        }
        if (product.metadata.definitions.region_worldwide) {
            regionWorldwide = product.metadata.definitions.region_worldwide;
        }

        const popoverClickRootClose = (
            <Popover id="popover-trigger-click-root-close" title="Regions">
                <Commonmark source={regionUk}/>
                <Commonmark source={regionEurope}/>
                <Commonmark source={regionWorldwideExcluding}/>
                <Commonmark source={regionWorldwide}/>
            </Popover>
        );

        const regionsList = map(product.metadata.regions, (name, key) => ({
            label: name,
            value: key,
        }));

        let maxTripDurationList = [];
        const maxTrips = get(product, 'metadata.max_trip_durations.' + coverLevel);
        if (maxTrips) {
            maxTripDurationList = map(maxTrips, (name, key) => ({
                label: name,
                value: key,
            }));
        } else {
            maxTripDurationList = map(product.metadata.annual_trip_durations,
                (name, key) => ({
                    label: name,
                    value: key,
                }));
        }

        const maxTripDefinition = product.metadata.definitions.max_trip_duration;
        const showMaxTripSelect = size(maxTripDurationList) > 1;

        const advancedDays = 31;
        const maxDate = moment().add(advancedDays, 'days');

        // @ts-ignore
        return (
            <Modal show={this.state.showUpSellModal} onHide={this.closeUpSellModal}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>Upgrading to {modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="section-group">
                        <div className="section">
                            Please confirm the region that you require cover for.
                            <p>
                                If your selected region differs from the region of your original
                                destination, this may affect the premium:
                            </p>
                            <Row className="margin-top">
                                <FormGroup controlId="destination">
                                    <Col componentClass={ControlLabel} sm={3}>
                                        {i18n.quote.tripDetails.region}{' '}
                                        <OverlayTrigger
                                            trigger="click"
                                            rootClose={true}
                                            placement="right"
                                            overlay={popoverClickRootClose}
                                        >
                                            <FaInfoCircle/>
                                        </OverlayTrigger>
                                    </Col>
                                    <Col sm={9} className="destinations-inputs">
                                        <Field
                                            name="region"
                                            component={SelectControl}
                                            options={regionsList}
                                            validate={[
                                                isRequired,
                                            ]}
                                        />
                                    </Col>
                                </FormGroup>
                            </Row>
                            <div className="margin-top">
                                {showMaxTripSelect && (
                                    <MaxTripDuration
                                        isInvalid={this.state.invalidFields}
                                        i18n={i18n}
                                        options={maxTripDurationList}
                                        definition={maxTripDefinition}
                                        withPopover={true}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="section">
                            <Row>
                                <FormGroup controlId="start-date">
                                    <Col componentClass={ControlLabel} xs={6}>
                                        Start Date
                                    </Col>
                                    <Col sm={6}>
                                        <div className="alternative-date-picker">
                                            <div className="field">
                                                <Field
                                                    name="startDate"
                                                    type="masked-date"
                                                    placeholder="dd/mm/yyyy"
                                                    component={FieldFormControl}
                                                />
                                            </div>
                                            <div className="icon">
                                                <Field
                                                    name="startDate"
                                                    maxDate={maxDate}
                                                    component={AlternativeDatePicker}
                                                    validate={[
                                                        isRequired,
                                                        isFutureDate,
                                                        isValidDate,
                                                        annualLimit(advancedDays),
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                </FormGroup>
                            </Row>
                            <div className="important margin-top">
                                <strong>IMPORTANT</strong> - Cancellation cover begins on the date you choose
                                to start your policy. If you already have a trip booked and you want to be covered
                                immediately, you should select today’s date. You can choose to start your policy
                                later (up to 31 days from today’s date) but there would not be any cancellation
                                cover between now and the start date you choose.
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        bsStyle="primary"
                        type="submit"
                        className="btn-remove-border"
                        onClick={this.handleModalSubmit}
                    >
                        Continue
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    private getBenefit = (benefits: IBenefit[], displayName, type?, subBenefitName?) => {
        let displayBenefit = '';
        let displayBenefitDescription = '';
        let subBenefit = [];
        let displaySubBenefit = '';

        benefits.forEach((benefit) => {
            if (benefit.displayName === displayName || benefit.name === displayName) {
                subBenefit = benefit.subBenefits;
                displayBenefit = benefit.text;
                displayBenefitDescription = benefit.description;
            }
            forEach(subBenefit, (item) => {
                displaySubBenefit = item.text;
            });
        });

        switch (type) {
            case 'subBenefit':
                return displaySubBenefit;
            case 'description':
                return displayBenefitDescription;
            default:
                return displayBenefit;
        }
    }

    private getSelectedBooleanOptions = (): { [key: string]: boolean } => {
        const opts = (this.getStableOptions() || {}) as Record<string, boolean | string | undefined>;
        const out: { [key: string]: boolean } = {};

        Object.keys(opts).forEach((k) => {
            if (k === 'hazardous_activities') { return; }
            if (typeof opts[k] === 'boolean') { out[k] = !!opts[k]; }
        });

        return out;
    }

    private getSelectedHazardPack = (): string => {
        const opts = (this.getStableOptions() || {}) as Record<string, boolean | string | undefined>;
        const formValue = opts.hazardous_activities as string | undefined;

        const appValue = get(this.props.quote, 'application.metadata.options.hazardous_activities');
        const productDefault = get(this.props.product, 'metadata.max_trip_durations.hazardous_activities.default');

        return (formValue || appValue || productDefault || 'pack1') as string;
    }

    private getStableQuotes = (): IQuotesCache => {
        return mergeQuotesIntoCache(this.state.quotesCache, this.props.quote.quotes) as IQuotesCache;
    }

    private getStableOptions = (): IOptionMap => {
        return mergeOptionsIntoCache(this.state.optionsCache, this.props.options) as IOptionMap;
    }

    private renderQuotes = (orderedTripTypes: any[]) => {
        const { quote, tripType: tripTypeValue, schemeId, channel, maxTripDuration  } = this.props;
        const { quoteOrder } = this.state;

        if (!tripTypeValue) {
            return null;
        }

        if (!orderedTripTypes || orderedTripTypes.length === 0) {
            return null;
        }

        const moreInfo = this.state.moreInfo;
        let breakingPoint = 2;
        if (channel.key === 'tpl-b2c') {
            breakingPoint = 3;
        }

        /**
         * STANDARD MODE
         */
        if (quoteOrder === 'standard') {
            const columnVms = buildStandardQuoteColumnsVm({
                orderedTripTypes,
                tripTypeValue,
                schemeId,
                quote: this.props.quote,
                signpost: quote.signpost,
                channel,
                maxTripDuration,
            });

            if (!columnVms || columnVms.length === 0) {
                return null;
            }

            return columnVms.map((colVm: IQuoteColumnVm, idx: number) => {
                if (!colVm) {
                    return <div key={idx} />;
                }

                const main = colVm.main;

                return (
                    <Col sm={4} key={colVm.key}>
                        <QuoteCard
                            containerClassName={main.containerClassName}
                            backgroundColor={main.backgroundColor}
                            title={main.title}
                            schemeTypeLabel={main.schemeTypeLabel}
                            gross={main.gross}
                            currency={main.currency}
                            schemeId={main.schemeId}
                            selectedSchemeId={schemeId}
                            onSchemeChange={this.handleSchemeIdChange}
                            standardExcess={main.standardExcess}
                            benefitsFlatten={main.benefitsFlatten}
                            breakingPoint={breakingPoint}
                            moreInfo={moreInfo}
                            moreInfoSign={this.state.moreInfoSign}
                            toggleMoreInfo={this.toggleMoreInfo}
                            downloadDocument={this.downloadDocument}
                            documents={main.documents}
                            signpost={quote.signpost}
                            channel={channel}
                            getBenefitText={this.getBenefit}
                            upsell={colVm.upsell}
                        />
                    </Col>
                );
            });
        }

        /**
         * SIDE-BY-SIDE MODE
         */
        if (quoteOrder === 'side-by-side' && orderedTripTypes.length === 1) {
            const cards = buildSideBySideVm({
                tripTypes: orderedTripTypes[0],
                tripTypeValue,
                schemeId,
                signpost: quote.signpost,
                channel,
            });
            const anyAvailable = cards && cards.some((c) => !!c);

            if (!anyAvailable) {
                return null;
            }

            return cards.map((vm: IQuoteCardVm, idx: number) => {
                if (!vm) {
                    return <div key={idx} />;
                }

                return (
                    <Col sm={4} key={vm.key}>
                        <QuoteCard
                            containerClassName={vm.containerClassName}
                            backgroundColor={vm.backgroundColor}
                            title={vm.title}
                            schemeTypeLabel={vm.schemeTypeLabel}
                            gross={vm.gross}
                            currency={vm.currency}
                            schemeId={vm.schemeId}
                            selectedSchemeId={schemeId}
                            onSchemeChange={this.handleSchemeIdChange}
                            standardExcess={vm.standardExcess}
                            benefitsFlatten={vm.benefitsFlatten}
                            breakingPoint={breakingPoint}
                            moreInfo={moreInfo}
                            moreInfoSign={this.state.moreInfoSign}
                            toggleMoreInfo={this.toggleMoreInfo}
                            downloadDocument={this.downloadDocument}
                            documents={vm.documents}
                            signpost={quote.signpost}
                            channel={channel}
                            getBenefitText={this.getBenefit}
                        />
                    </Col>
                );
            });
        }

        return null;
    }

    private toggleMoreInfo = () => {
        if (this.state.moreInfoSign === 'glyphicon-plus-sign') {
            this.setState((prevState) => ({
                moreInfoSign: 'glyphicon-minus-sign',
            }));
        } else {
            this.setState((prevState) => ({
                moreInfoSign: 'glyphicon-plus-sign',
            }));
        }

        this.setState((prevState) => ({
            moreInfo: !prevState.moreInfo,
        }));
    }

    private downloadDocument = (benefitsKey) => () => {
        const IPID = 'Insurance Product Information Document';
        const toDownload = benefitsKey[IPID];
        if (window) {
            window.open(toDownload, '_blank');
        }
    }

    private handleHazardousSelect = (packKey: string) => {
        this.handleHazardousPackClick(packKey)();
    }

    private handleSchemeIdChange: EventWithDataHandler<Event> = (event, newValue) => {
        const {dispatch, change} = this.props;
        event.preventDefault();
        dispatch(change('schemeId', newValue));
        this.resetOptionsForNewScheme();
        this.recalculatePrice();
        this.checkForUpSell(newValue);
    }

    public renderLoader() {
        return (

            <div className="schemes-loader text-center">
                <Loading/>
                <p>
                    Please wait whilst we generate your premiums...
                </p>
            </div>
        );
    }

    private resetOptionsForNewScheme = () => {
        const { change } = this.props;
        const allOptions = this.getAllOptions();
        if (!allOptions) { return; }

        (Object.entries(allOptions) as Array<[string, AvailableOptionsMap[string]]>).forEach(
            ([fieldKey, opt]) => {
                if (opt.key === 'hazardous_activities') { return; }
                if (opt.input_type === 'boolean') {
                    change('options.' + fieldKey, false);
                }
            },
        );

        const hazardousOptionEntry =
            (Object.entries(allOptions) as Array<[string, AvailableOptionsMap[string]]>).find(
                ([, opt]) => opt.key === 'hazardous_activities',
            );

        if (hazardousOptionEntry) {
            const [hazFieldKey, hazOpt] = hazardousOptionEntry;
            const defaultPack = hazOpt.default ? hazOpt.default : 'pack1';
            change('options.' + hazFieldKey, defaultPack);
        } else {
            change('options.hazardous_activities', 'pack1');
        }
    }

    private handleHazardousPackClick = (packKey: string) => () => {
        const { change } = this.props;

        this.setState(
            (prev) => ({
                ...prev,
                optionsCache: {
                    ...(prev.optionsCache || {}),
                    hazardous_activities: packKey,
                },
            }),
            () => {
                persistedOptionsCache = this.state.optionsCache;
                change('options.hazardous_activities', packKey);
                this.recalculatePrice();
            },
        );
    }
    private getAllOptions(): AvailableOptionsMap | undefined {
        const { quote, schemeId, product } = this.props;
        const premiums = get(quote, 'application.premiums', []);

        const matchingPremium = premiums.find((p) => p && p.scheme && p.scheme.id === schemeId);
        if (!matchingPremium) { return undefined; }

        const info = matchingPremium.information;
        if (!info || !info.availableOptions) { return undefined; }

        let options = info.availableOptions;

        const customers = get(quote, 'application.customers', get(quote, 'application.customer', []));
        const ageLimitForProduct = {
            CSIS: 65,
            TPL: 70,
            CAMC: 65,
        };

        const anyOver65 = customers.some((customer) => {
            const dobDate = parseDob(customer.dob);
            return dobDate
                ? getAge(dobDate) >= ageLimitForProduct[product.productCode]
                : false;
        });

        if (anyOver65) {
            const { winter_sports, ...rest } = options;
            options = rest;
        }

        if (product.productCode === 'CSIS') {
            const region = get(quote, 'application.region', '');

            const anyOver70 = customers.some((customer) => {
                const dobDate = parseDob(customer.dob);
                return dobDate ? getAge(dobDate) > 70 : false;
            });

            if (anyOver70 || ['worldwide', 'worldwide_excluding'].includes(region)) {
                const { trip_extension, ...rest } = options;
                options = rest;
            }
        }

        return options;
    }
    private toggleBooleanOption = (fieldKey: string) => {
        this.handleQuestionOptionClick(fieldKey)();
    }

    public render() {
        const {
            handleSubmit,
            quote,
            channel,
            product,
            submitting,
            submitFailed,
            tripType: tripTypeValue,
            valid,
            onBack,
            maxTripDuration,
        } = this.props;

        const vm = this.buildVm();

        const left = (
            <LeftSideHelper
                quote={quote}
                tripType={tripTypeValue}
                product={product}
                quoteOrder={this.state.quoteOrder}
                moreInfo={this.state.moreInfo}
                renderDefinition={this.props.renderDefinition}
                orderedTripTypes={vm.orderedTripTypes}
                channel={channel}
                showAnnual={shouldShowAnnualTile(quote, maxTripDuration)}
            />
        );

        if (!vm.hasFinishedCalculation) {
            return this.renderLoader();
        }

        const channelKey = channel.key;

        return (
            <Form horizontal={true} onSubmit={handleSubmit} autoComplete="off">
                {!vm.noAvailableQuotes ? (
                    <div>
                        <div className="scheme-choice-section">
                            <div className="section-title">
                                {channel.key === 'camc-b2c' ? (
                                    <h1>Your Quote</h1>
                                ) : (
                                    <h3>Your Quote</h3>
                                )}
                            </div>

                            <div className="available-quotes">
                                {vm.coverChoices ? (
                                    <Row>
                                        {left}
                                        {vm.hasFinishedCalculation && vm.coverChoices}
                                    </Row>
                                ) : (
                                    <div className="unable-box">
                                        <NoCoverAvailable
                                            promptSingleQuote={tripTypeValue === 'single'}
                                            channel={channel}
                                            quoteReference={quote.application.quoteReference}
                                            callMessage={vm.renderCallMessage}
                                            signpost={quote.signpost}
                                        />
                                    </div>
                                )}
                            </div>

                            {this.renderUpSellModal()}
                        </div>

                        {vm.showAdditionalOptions && (
                            <div className="steps-container">
                                <div className="section-title">
                                    <h1>Additional Options</h1>
                                </div>

                                <div className="options-section-group">
                                    {vm.hasFinishedCalculation ? (
                                        <div>
                                            <OptionsList
                                                optionsMap={vm.mappedAllObjects}
                                                localOptions={vm.selectedBooleans}
                                                quote={quote}
                                                isCalculating={!!quote.isCalculating}
                                                showPartCMessageModal={this.state.showPartCMessageModal}
                                                renderDefinition={this.props.renderDefinition}
                                                recalculatePrice={this.recalculatePrice}
                                                onToggleBooleanOption={this.toggleBooleanOption}
                                                renderPartCMessageModal={this.renderPartCMessageModal}
                                                channelKey={channelKey}
                                            />

                                            <HazardousPackSelector
                                                hazardousOptions={vm.hazardousOptions}
                                                selectedPackKey={vm.selectedHazardPack}
                                                isCalculating={!!quote.isCalculating}
                                                onSelectPack={this.handleHazardousSelect}
                                                channelKey={channelKey}
                                                product={this.props.product}
                                                renderDefinition={this.props.renderDefinition}
                                            />
                                        </div>
                                    ) : (
                                        <Loading/>
                                    )}
                                </div>

                                <div className="btn-bar">
                                    <Button
                                        bsStyle="primary"
                                        type="submit"
                                        className="pull-right btn-remove-border"
                                        disabled={submitting || (submitFailed && !valid)}
                                    >
                                        Continue to next step <FaChevronRight/>
                                    </Button>
                                    <Button
                                        type="button"
                                        bsStyle="primary" className="btn-remove-border" onClick={onBack}>
                                        Back
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="steps-container unable-box">
                        <h1>Sorry, we are unable to provide you with a quote</h1>
                    </div>
                )}
            </Form>
        );
    }
}

const selector = formValueSelector('quote');
let previousValues: IPolicyApplication | undefined;

export default flow([
    reduxForm<IPolicyApplication, ISchemeChoiceFormOwnProps>({
        form: 'quote',
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: false,
        onSubmitFail: () => {
            setTimeout(() => scrollToFirstError({ offset: 120 }), 0);
        },
        onChange: (
            values: IPolicyApplication,
            dispatch,
            props: ISchemeChoiceFormProps,
        ) => {
            if (!previousValues) {
                previousValues = values;
                return;
            }

            let shouldRecalculate = false;

            if (difference(values.destinations, previousValues.destinations).length > 0) {
                shouldRecalculate = true;
            }

            if (values.region !== previousValues.region) {
                shouldRecalculate = true;
            }

            if (shouldRecalculate) {
                props.actions.quote.recalculateScreening();
            }

            previousValues = values;
        },
    }),

    connect(
        (state: IStore) => {
            const quote = state.quote;

            return {
                options: selector(state, 'options'),
                tripType: selector(state, 'tripType'),
                maxTripDuration: selector(state, 'maxTripDuration'),
                schemeId: selector(state, 'schemeId'),
                startDate: selector(state, 'startDate'),
                endDate: selector(state, 'endDate'),
                travellers: selector(state, 'travellers'),
                region: selector(state, 'region'),
                password: selector(state, 'password'),
                passwordConfirmation: selector(state, 'passwordConfirmation'),
                steps: selector(state, 'steps'),

                quote,
                auth: state.auth,
                channel: state.branding.channel,
            };
        },

        (dispatch) => ({
            dispatch,
            actions: {
                submit: bindActionCreators(submit, dispatch),
                quote: bindActionCreators({ ...quoteActions }, dispatch),
                push: bindActionCreators(push, dispatch),
                recalculateScore: bindActionCreators(recalculateScore, dispatch),
            },
        }),
    ),
])(SchemeChoiceForm);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/scenes/SchemeChoiceB/index.tsx